import './task.scss'

import * as _ from 'lodash'
import { combineLatest, timer } from 'rxjs'
import { map } from 'rxjs/operators'
import { Component, Watch } from 'vue-property-decorator'
import { Route } from 'vue-router'
import {
    faAngleDown,
    faAngleRight,
    faCircleNotch,
    faExclamationTriangle,
    faCheck,
} from '@fortawesome/free-solid-svg-icons'
import { BModal } from 'bootstrap-vue'

import { Item, Movement, MovementTypes, ThesaurusTerm, Featured, Task, TaskLifecycleStates, getTranslatedString } from '@skinsoft/types'
import { itemSelectors, taskSelectors, thesaurusTermSelectors } from '@skinsoft/selectors'
import {
    ItemDataStore,
    itemSorters,
    MovementDataStore,
    TaskDataStore,
    ThesaurusTermDataStore,
} from '@skinsoft/entity'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { TaskFeaturedCard } from '@skinsoft/entity-ui'
import { ItemCard, PullToRight } from '@skinsoft/ui'
import {
    CREATE_COMPLEX_MOVEMENT_FROM_TASK_TOPIC,
    CREATE_SIMPLE_MOVEMENT_FROM_TASK_TOPIC,
    REJECT_TASK_TOPIC,
    START_TASK_TOPIC,
    TERMINATE_TASK_TOPIC,
} from '../../../busTopics'
import { router, Routes } from '../../../router'
import { CommentEditor } from '../../component/comment-editor'
import { AdvanceTaskModal } from '../../component/advance-task-modal'

@Component({
    components: {
        sCommentEditor: CommentEditor,
        sItemCard: ItemCard,
        sPullToRight: PullToRight,
        sTaskFeaturedCard: TaskFeaturedCard,
        advanceTaskModal: AdvanceTaskModal,
    },
    template: require('./task.html'),
})
export class TaskPage extends ReactiveVue {
    public $itemDataStore: ItemDataStore
    public $movementDataStore: MovementDataStore
    public $route: Route
    public $taskDataStore: TaskDataStore
    public $thesaurusTermDataStore: ThesaurusTermDataStore
    public angleDownIcon = faAngleDown
    public angleRightIcon = faAngleRight
    public exclamationTriangleIcon = faExclamationTriangle
    public itemSelectors = itemSelectors
    public spinnerIcon = faCircleNotch
    public checkIcon = faCheck
    public targetLifecycle = TaskLifecycleStates.IN_PROGRESS

    public hasPendingMovement = false
    public items: Item[] = [null, null, null] as any[]
    public task: Task = null as any
    public thesaurusTerm: ThesaurusTerm[] = []
    public showCollapse = false
    public operatorCommentPersisting = false
    public hasUnsavedComment = false

    private movementType: MovementTypes | null = null

    public $refs: {
        fakeEntity: HTMLDivElement
        item: ItemCard[]
        puller: PullToRight
        ribbon: HTMLDivElement
        viewport: HTMLDivElement
        advanceTaskModal: AdvanceTaskModal,
        createMovementModal: BModal,
    }

    get description() {
        if(this.thesaurusTerm && this.thesaurusTerm.length > 0) {
            let description = ''
            for(const thesaurusTerm of this.thesaurusTerm) {
                const preferredTerm = getTranslatedString(thesaurusTermSelectors.preferredTerm(thesaurusTerm), this.$i18n.locale)
                description += preferredTerm ? preferredTerm + ', ' : ''
            }
            return description.charAt(description.length - 2) === ',' ? description.slice(0, -2) : description
        }
        return null
    }

    get precisionRequest() {
        return taskSelectors.precisionRequest(this.task)
    }

    get operatorComment() {
        return taskSelectors.operatorComment(this.task, this.$i18n.locale)
    }

    public mounted() {
        this.subscribeToBus(CREATE_SIMPLE_MOVEMENT_FROM_TASK_TOPIC, () => {
            this.movementType = MovementTypes.SIMPLE
            this.$refs.createMovementModal.show()

        })
        this.subscribeToBus(CREATE_COMPLEX_MOVEMENT_FROM_TASK_TOPIC, () => {
            this.movementType = MovementTypes.COMPLEX
            this.$refs.createMovementModal.show()

        }),
        this.subscribeToBus(REJECT_TASK_TOPIC, () => this.rejectTask())
        this.subscribeToBus(START_TASK_TOPIC, () => this.startTask())
        this.subscribeToBus(TERMINATE_TASK_TOPIC, () => this.terminateTask())
        this.subscribeToEntity()
    }

    public rejectTask() {
        this.targetLifecycle = TaskLifecycleStates.PLANNED
        if (this.$refs.advanceTaskModal) {
            this.$refs.advanceTaskModal.open()
        }
    }

    public startTask() {
        this.targetLifecycle = TaskLifecycleStates.IN_PROGRESS
        if (this.$refs.advanceTaskModal) {
            this.$refs.advanceTaskModal.open()
        }
    }

    public terminateTask() {
        this.targetLifecycle = TaskLifecycleStates.FINALIZED
        if (this.$refs.advanceTaskModal) {
            this.$refs.advanceTaskModal.open()
        }
    }

    public subscribeToEntity() {
        this.subscribeTo(
            this.$taskDataStore.fetchOne(this.$route.params.id),
            (task) => {
                this.task = task
            },
            'task',
        )
    }

    public subscribeToItems() {
        const linkableItems = taskSelectors.linkableItems(this.task)

        if (linkableItems.length === 0) {
            this.items = []
            return
        }

        this.subscribeTo(
            combineLatest(
                ...linkableItems
                    .filter(item => item.linkableItem.linkableItem)
                    .map((item) => {
                        return this.$itemDataStore.fetchIfHasAccess(item.linkableItem.linkableItem!)
                    }),
            ).pipe(
                map(items => items.filter(_.identity))
            ),
            (items) => {
                this.items = items.sort(itemSorters.byRealTimeLocation) as Item[]
            },
            'items',
        )
    }

    public subscribeToThesaurusTerm() {
        const ids = taskSelectors.associatedTasks(this.task)

        if (!ids) {
            this.thesaurusTerm = []
            return
        }

        this.subscribeTo(
            this.$thesaurusTermDataStore.fetchMany(ids),
            (thesaurusTerms) => {
                if(thesaurusTerms) {
                    if(thesaurusTerms.allData && thesaurusTerms.allData.length > 0) {
                        this.thesaurusTerm = thesaurusTerms.allData
                        return
                    } 
                    else if(thesaurusTerms.results && thesaurusTerms.results.length > 0) {
                        this.thesaurusTerm = thesaurusTerms.results
                        return
                    }
                } 
                this.thesaurusTerm = []
            },
            'thesaurusTerm',
        )
    }

    @Watch('$route', { deep: true })
    public onRouteChange() {
        // to hide the reset of the puller, we reset the state of this page to make it look like the same as the mockup view
        this.items = [null, null, null] as any[]
        this.$refs.puller.reset()

        this.unsubscribeFrom('task')
        this.subscribeToEntity()
    }

    @Watch('task', { deep: true })
    public onEntityChange() {
        this.unsubscribeFrom('items')
        this.subscribeToItems()
        this.unsubscribeFrom('thesaurusTerm')
        this.subscribeToThesaurusTerm()
    }

    public onCommentEditorChange(operatorComment: string) {
        _.set(
            this.task,
            `taskSchema.operatorComment.translatedString.${this.$i18n.locale}`,
            operatorComment,
        )
        this.hasUnsavedComment = true
    }

    public async onCommentEditorSave() {
        this.operatorCommentPersisting = true
        try {
            await this.$taskDataStore.updateOperatorComment(
                this.task.id,
                this.operatorComment,
                this.$i18n.locale,
            )
            this.hasUnsavedComment = false
        } finally {
            this.operatorCommentPersisting = false
        }
    }

    public onPulled() {
        // the user has reach pull-right non return point, we must transition to the next entity
        const next = this.$route.query.next as any
        const id = (next as string[]).shift() as string

        requestAnimationFrame(() =>
            router.push({
                name: this.$route.name || '',
                params: { id },
                query: (next.length > 0 ? { next } : {}) as Record<string, string>,
            }),
        )
    }

    public toggleCollapse([_, event]: [Featured, MouseEvent]) {
        if (event.target && (event.target as HTMLElement).id === 'operatorComment') {
            return
        }

        this.showCollapse = !this.showCollapse
    }

    private async createMovementFromTask(
        movementType: MovementTypes.SIMPLE | MovementTypes.COMPLEX = MovementTypes.SIMPLE,
    ) {
        this.movementType = null
        this.hasPendingMovement = true

        let movement: Movement
        try {
            movement = await (this.$movementDataStore as MovementDataStore).createMovement(
                movementType,
                this.task.id,
            )
        } finally {
            this.hasPendingMovement = false
        }

        if (movement) {
            router.push({
                name: Routes.MOVEMENT,
                params: { id: movement.id },
            })
        }
    }

    private refreshTask() {
        this.unsubscribeFrom('task')
        timer(1000).subscribe(this.subscribeToEntity)
    }

    public get state() {
        return taskSelectors.state(this.task)
    }
}
